import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Keeps a page's view state and its query string in sync, in both directions,
 * so every view has an address that can be pasted to someone else.
 *
 * Written for the Kubernetes console and reused by the Argo CD and OpenBao
 * pages — the hard parts are the same everywhere and are easy to get subtly
 * wrong:
 *
 *  - **Both directions.** Writing state to the URL without reading it back
 *    breaks Back and Forward: the page rewrites the address forward the
 *    instant the reader navigates. The `adopt` callback folds external
 *    changes into state, and an in-flight adoption suppresses exactly one
 *    write so the two never stomp each other.
 *  - **History that means something.** Moving to another view earns an
 *    entry; typing in a filter replaces one. Otherwise Back walks backwards
 *    through keystrokes.
 *  - **Settling is not navigation.** A page that fills in its own defaults
 *    on load (a cluster, a namespace) must not consume history entries the
 *    reader would then have to press Back through.
 */
export interface UrlStateOptions<S> {
  /** Current view state, memoised by the caller. */
  state: S;
  /** Reads state out of a query string. Must be pure. */
  parse: (params: URLSearchParams) => S;
  /** Writes state to a query string, omitting anything at its default. */
  serialize: (state: S) => URLSearchParams;
  /** Applies externally-arrived state — Back/Forward, or a pasted link. */
  adopt: (state: S) => void;
  /** Keys whose change means the reader navigated rather than refined. */
  navigationKeys: readonly (keyof S)[];
  /**
   * True when a change is the page filling in its own defaults rather than
   * the reader moving. Those changes replace the history entry.
   */
  isSettling?: (previous: S, next: S) => boolean;
}

export interface UrlState<S> {
  /** State parsed from the URL at mount, for seeding useState initialisers. */
  initial: S;
  /** Absolute link to the current view. */
  shareUrl: string;
  /** Copies `shareUrl`, falling back to reporting it when the clipboard is denied. */
  copyShareUrl: (report: (message: string) => void) => Promise<void>;
}

export function useUrlState<S>(options: UrlStateOptions<S>): UrlState<S> {
  const { state, parse, serialize, adopt, navigationKeys, isSettling } =
    options;
  const [params, setParams] = useSearchParams();

  // Read once. From here the URL is written from state; `adopt` handles the
  // cases where something else changes it.
  const initialRef = useRef<S>();
  if (initialRef.current === undefined) initialRef.current = parse(params);

  const previousRef = useRef<S>(initialRef.current);
  const lastWrittenRef = useRef(serialize(initialRef.current).toString());
  const adoptingRef = useRef(false);

  // Keep the callbacks in refs so a caller that rebuilds them every render
  // does not restart the effects (and re-adopt) on every keystroke.
  const parseRef = useRef(parse);
  const adoptRef = useRef(adopt);
  parseRef.current = parse;
  adoptRef.current = adopt;

  useEffect(() => {
    const incoming = params.toString();
    if (incoming === lastWrittenRef.current) return;
    lastWrittenRef.current = incoming;
    adoptingRef.current = true;
    adoptRef.current(parseRef.current(params));
  }, [params]);

  useEffect(() => {
    // The adoption above queued state updates this render has not seen.
    // Writing now would put the pre-adoption state straight back over the
    // URL the reader just navigated to.
    if (adoptingRef.current) {
      adoptingRef.current = false;
      previousRef.current = state;
      return;
    }
    const next = serialize(state);
    const serialized = next.toString();
    if (serialized === params.toString()) {
      previousRef.current = state;
      return;
    }
    const previous = previousRef.current;
    const settling = isSettling?.(previous, state) ?? false;
    const navigated =
      !settling && navigationKeys.some(key => previous[key] !== state[key]);
    previousRef.current = state;
    lastWrittenRef.current = serialized;
    setParams(next, { replace: !navigated });
  }, [state, params, setParams, serialize, navigationKeys, isSettling]);

  const shareUrl = useMemo(() => {
    const query = serialize(state).toString();
    return `${window.location.origin}${window.location.pathname}${
      query ? `?${query}` : ''
    }`;
  }, [serialize, state]);

  const copyShareUrl = useCallback(
    async (report: (message: string) => void) => {
      try {
        await navigator.clipboard.writeText(shareUrl);
        report('Link copied');
      } catch {
        // Clipboard access is denied outside a secure context; showing the
        // URL still lets the reader copy it by hand.
        report(shareUrl);
      }
    },
    [shareUrl],
  );

  return { initial: initialRef.current, shareUrl, copyShareUrl };
}

// ─── serialization helpers ───────────────────────────────────────────────────

/** Adds `value` under `key` unless it is empty or equals `fallback`. */
export function setParam(
  params: URLSearchParams,
  key: string,
  value: string,
  fallback = '',
): void {
  if (value && value !== fallback) params.set(key, value);
}

/** Reads a value constrained to a known set, falling back when it is not. */
export function oneOf<T extends string>(
  value: string | null,
  allowed: readonly T[],
  fallback: T,
): T {
  return value && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

/** Joins a composite object key — `group/kind/namespace/name` and friends. */
export function joinKey(...parts: (string | undefined)[]): string {
  return parts.map(part => part ?? '').join('/');
}

/** Splits a composite key back into exactly `count` parts. */
export function splitKey(value: string, count: number): string[] {
  const parts = value.split('/');
  while (parts.length < count) parts.push('');
  return parts.slice(0, count);
}
